import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Sheet } from "@/components/shell/Sheet";
import { Button } from "@/components/ui/Button";
import { ChildLoginQR } from "./ChildLoginQR";
import { SlugClaimForm } from "./SlugClaimForm";
import { ChorePresetPicker } from "./ChorePresetPicker";
import { useAuth } from "@/lib/auth/context";
import { useHousehold } from "@/lib/hooks/useHousehold";
import { createMemberAccount } from "@/lib/db/members";
import { createChore } from "@/lib/db/chores";
import { memberUsernameSchema } from "@/lib/schemas";
import { toast } from "@/components/shell/Toaster";
import { normalizeUsername } from "@/lib/utils/slug";
import { pluralizeZadania } from "@/lib/utils/plural";
import type { ChorePresetChore } from "@/lib/presets/chorePresets";

const childSchema = z.object({
  displayName: z.string().min(1, "Wpisz imię"),
  relation: z.string().max(20).optional(),
  // Wiek (4-18) jako string z formularza, parse na liczbę w submit.
  // Optional — można pominąć, wtedy preset picker nie auto-zaznaczy.
  age: z
    .string()
    .optional()
    .refine(
      (s) => !s || /^(?:[4-9]|1[0-8])$/.test(s.trim()),
      "Wiek 4-18 lat",
    ),
  username: memberUsernameSchema,
  password: z
    .string()
    .min(8, "Min. 8 znaków")
    .regex(/[a-zA-Z]/, "Musi zawierać literę")
    .regex(/\d/, "Musi zawierać cyfrę"),
});
type ChildValues = z.infer<typeof childSchema>;

const RELATION_SUGGESTIONS = ["Syn", "Córka"];

// Proste, przyjazne słowa bez polskich znaków — dziecko wpisze je bez męki
// na klawiaturze telefonu. Słowo (≥6) + 2 cyfry = min. 8 znaków,
// litera + cyfra: spełnia politykę hasła dziecka.
const FRIENDLY_PASSWORD_WORDS = [
  "sloneczko",
  "truskawka",
  "rowerek",
  "biedronka",
  "motylek",
  "jagodka",
  "traktorek",
  "koniczek",
  "zielonka",
  "siankowo",
];

function generateChildPassword(): string {
  const word =
    FRIENDLY_PASSWORD_WORDS[
      Math.floor(Math.random() * FRIENDLY_PASSWORD_WORDS.length)
    ];
  const num = Math.floor(Math.random() * 90) + 10;
  return `${word}${num}`;
}

type Props = {
  open: boolean;
  onClose: () => void;
};

type CreatedChild = {
  uid: string;
  displayName: string;
  /** Wiek policzony z birthYear — `undefined` gdy rodzic pominął pole. */
  age?: number;
  /** Dane logowania do karty QR (krok 4) — tylko w pamięci sheetu. */
  username: string;
  password: string;
};

/**
 * 3-krokowy Sheet do dodawania dziecka inline na Dashboardzie.
 *
 * Krok 1 (warunkowy): claim pseudonima rodziny gdy household jeszcze nie ma
 * slug.
 *
 * Krok 2: form dziecka (imię + relacja + wiek + username + hasło). Wiek
 * zapisywany jako `birthYear` w members/{uid}; używany do auto-wyboru
 * preset chore'ów w kroku 3.
 *
 * Krok 3: ChorePresetPicker — gotowe zestawy zadań dopasowane do wieku.
 * Tap "Dodaj zaznaczone" → batch createChore z assignedTo=newChildUid.
 * Tap "Pomiń" → sheet zamyka się, dashboard dziecka startuje pusto
 * (świadoma decyzja — żaden user nie jest zmuszony).
 */
export function AddChildSheet({ open, onClose }: Props) {
  const { userDoc, firebaseUser } = useAuth();
  const hid = userDoc?.householdId;
  const { household } = useHousehold(hid);

  const [serverError, setServerError] = useState<string | null>(null);
  const [step, setStep] = useState<"form" | "presets" | "credentials">("form");
  const [createdChild, setCreatedChild] = useState<CreatedChild | null>(null);
  // Po „Wygeneruj" pokazujemy hasło jawnie — rodzic musi je przeczytać dziecku.
  const [revealPassword, setRevealPassword] = useState(false);

  const form = useForm<ChildValues>({
    resolver: zodResolver(childSchema),
  });

  // Reset stanu gdy sheet się zamyka — następne otwarcie startuje od zera.
  // Inaczej parent po dodaniu dziecka A widziałby przy kolejnym otwarciu
  // stan z A (preset picker, gotowy createdChild) zamiast pustego formularza.
  useEffect(() => {
    if (!open) {
      form.reset();
      setStep("form");
      setCreatedChild(null);
      setServerError(null);
      setRevealPassword(false);
    }
  }, [open, form]);

  const inputCls =
    "mt-1 w-full h-11 rounded-[12px] border border-line bg-card px-3 text-sm focus:outline-none focus:border-accent";

  const onSubmit = form.handleSubmit(async (values) => {
    if (!hid || !household?.slug) return;
    setServerError(null);
    try {
      const ageNum = values.age?.trim()
        ? parseInt(values.age.trim(), 10)
        : undefined;
      const birthYear =
        ageNum != null ? new Date().getFullYear() - ageNum : undefined;
      const { uid: newUid } = await createMemberAccount({
        hid,
        identity: {
          kind: "username",
          username: values.username,
          householdSlug: household.slug,
        },
        password: values.password,
        displayName: values.displayName,
        relation: values.relation?.trim() || undefined,
        role: "child",
        birthYear,
      });
      setCreatedChild({
        uid: newUid,
        displayName: values.displayName,
        age: ageNum,
        username: values.username,
        password: values.password,
      });
      setStep("presets");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Błąd";
      if (msg.includes("email-already-in-use")) {
        setServerError("Ta nazwa użytkownika jest już zajęta w Twojej rodzinie.");
      } else {
        setServerError(msg);
      }
    }
  });

  const handlePresetConfirm = async (chores: ChorePresetChore[]) => {
    if (!hid || !firebaseUser || !createdChild) return;
    try {
      await Promise.all(
        chores.map((c) =>
          createChore(hid, firebaseUser.uid, {
            ...c,
            assignedTo: createdChild.uid,
            active: true,
          }),
        ),
      );
      toast.success(
        `Dodano ${createdChild.displayName} i ${chores.length} ${pluralizeZadania(chores.length)}`,
      );
      setStep("credentials");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Błąd przy dodawaniu zadań";
      toast.error(msg);
    }
  };

  const handlePresetSkip = () => {
    if (createdChild) {
      toast.success(`Dodano dziecko: ${createdChild.displayName}`);
    }
    setStep("credentials");
  };

  // Krok 1: claim slug rodziny gdy brakuje
  if (!household?.slug) {
    return (
      <Sheet open={open} onClose={onClose} title="Pseudonim rodziny">
        <div className="space-y-3">
          <p className="text-xs text-muted">
            Dziecko nie loguje się przez email tylko przez pseudonim rodziny +
            swoją nazwę użytkownika. Najpierw wybierz pseudonim — krótkie,
            łatwe słowo (np. nazwisko).
          </p>
          <SlugClaimForm
            onClaimed={() => {
              // Po claim Sheet pozostaje otwarty, useHousehold dostarczy
              // nowy slug realtime, krok 2 (child form) wyrenderuje się
              // w następnym renderze.
            }}
          />
        </div>
      </Sheet>
    );
  }

  // Krok 4: karta logowania — QR z jednorazowym tokenem (ChildLoginQR),
  // dziecko skanuje zwykłym aparatem i jest zalogowane bez wpisywania.
  // Hasło pokazane jako fallback (ręczne logowanie, wygasły token) —
  // tylko w pamięci sheetu, po zamknięciu nie pokażemy go ponownie.
  if (step === "credentials" && createdChild && household?.slug) {
    return (
      <Sheet
        open={open}
        onClose={onClose}
        title={`Logowanie: ${createdChild.displayName}`}
      >
        <div className="space-y-4">
          <p className="text-xs text-muted leading-relaxed">
            Dziecko celuje w ten kod <strong>zwykłym aparatem</strong> swojego
            telefonu (bez instalowania czegokolwiek) i tapnie link, który się
            pojawi — Sianko zaloguje je od razu. Gdyby kod wygasł albo aparat
            nie czytał kodów QR, poniżej są dane do logowania ręcznego.
          </p>
          <ChildLoginQR
            childUid={createdChild.uid}
            username={createdChild.username}
            slug={household.slug}
          />
          <div className="rounded-[12px] bg-soft p-3 text-sm space-y-1.5">
            <div className="flex justify-between gap-2">
              <span className="text-muted">Rodzina</span>
              <strong className="font-num">{household.slug}</strong>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted">Nazwa użytkownika</span>
              <strong className="font-num">{createdChild.username}</strong>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted">Hasło</span>
              <strong className="font-num">{createdChild.password}</strong>
            </div>
          </div>
          <p className="text-[11px] text-muted leading-relaxed">
            Zapisz hasło zanim zamkniesz — nie pokażemy go ponownie. Zawsze
            możesz ustawić nowe w Ustawieniach → Domownicy.
          </p>
          <Button variant="accent" size="lg" block onClick={onClose}>
            Gotowe
          </Button>
        </div>
      </Sheet>
    );
  }

  // Krok 3: preset picker po sukcesie kroku 2
  if (step === "presets" && createdChild) {
    return (
      <ChorePresetPicker
        open={open}
        childName={createdChild.displayName}
        childAge={createdChild.age}
        onConfirm={handlePresetConfirm}
        onSkip={handlePresetSkip}
      />
    );
  }

  // Krok 2: form dziecka
  return (
    <Sheet open={open} onClose={onClose} title="Dodaj dziecko">
      <form onSubmit={onSubmit} className="space-y-3">
        <p className="text-xs text-muted">
          Dziecko zaloguje się przez nazwę użytkownika + pseudonim rodziny:{" "}
          <span className="font-num font-semibold">{household.slug}</span>.
        </p>

        <label className="block">
          <span className="text-xs font-medium text-muted">Imię</span>
          <input
            placeholder="np. Kasia"
            className={inputCls}
            {...form.register("displayName")}
          />
          {form.formState.errors.displayName && (
            <p className="mt-1 text-xs text-danger">
              {form.formState.errors.displayName.message}
            </p>
          )}
        </label>

        <label className="block">
          <span className="text-xs font-medium text-muted">Relacja</span>
          <select className={inputCls} {...form.register("relation")}>
            <option value="">— wybierz —</option>
            {RELATION_SUGGESTIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-muted">
            Wiek <span className="text-muted/70">(opcjonalnie)</span>
          </span>
          <input
            type="number"
            inputMode="numeric"
            min={4}
            max={18}
            placeholder="np. 8"
            className={`${inputCls} font-num`}
            {...form.register("age")}
          />
          {form.formState.errors.age ? (
            <p className="mt-1 text-xs text-danger">
              {form.formState.errors.age.message}
            </p>
          ) : (
            <p className="mt-1 text-[11px] text-muted">
              Pomaga dobrać zestaw zadań na start. Można pominąć.
            </p>
          )}
        </label>

        <label className="block">
          <span className="text-xs font-medium text-muted">
            Nazwa użytkownika
          </span>
          <input
            placeholder="np. kasia"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className={inputCls}
            {...form.register("username", {
              // Live normalize na onChange — user pisze "Kasia" → wpisuje
              // się "kasia". RHF setValue żeby form value był spójny.
              onChange: (e) => {
                const next = normalizeUsername(e.target.value);
                form.setValue("username", next, { shouldValidate: false });
                e.target.value = next;
              },
            })}
          />
          {form.formState.errors.username && (
            <p className="mt-1 text-xs text-danger">
              {form.formState.errors.username.message}
            </p>
          )}
          <p className="mt-1 text-[11px] text-muted">
            3-20 znaków: a-z, 0-9, myślnik. Logowanie:{" "}
            <span className="font-num">
              {form.watch("username") || "kasia"}@{household.slug}
            </span>
          </p>
        </label>

        <label className="block">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted">Hasło</span>
            <button
              type="button"
              onClick={() => {
                form.setValue("password", generateChildPassword(), {
                  shouldValidate: true,
                });
                setRevealPassword(true);
              }}
              className="text-xs text-accent font-medium hover:underline"
            >
              Wygeneruj proste hasło
            </button>
          </div>
          <input
            type={revealPassword ? "text" : "password"}
            autoComplete="new-password"
            placeholder="min. 8 znaków, litera i cyfra"
            className={inputCls}
            {...form.register("password")}
          />
          {form.formState.errors.password ? (
            <p className="mt-1 text-xs text-danger">
              {form.formState.errors.password.message}
            </p>
          ) : (
            <p className="mt-1 text-[11px] text-muted">
              Min. 8 znaków, litera i cyfra. Proste słowo + cyfry wystarczy —
              dziecko musi umieć je wpisać.
            </p>
          )}
        </label>

        {serverError && (
          <p className="text-xs text-danger">{serverError}</p>
        )}

        <div className="grid grid-cols-2 gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button type="submit" variant="accent" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? "Dodaję…" : "Dodaj dziecko"}
          </Button>
        </div>
      </form>
    </Sheet>
  );
}
